import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowsLeftRight, Bank, CalendarBlank, CalendarX, Cake, DeviceMobile, Drop, EnvelopeSimple, Flame, House, IdentificationCard, Phone, User, Users, Vault,
} from '@phosphor-icons/react';
import {
  Chip, FeldRaster, LivePreviewCard, SchalterZeile, SearchSelect, SegmentedControl, StepForm, Stepper, TextFeld, ToastPill, useToastPill,
} from '../../../../components/immobilien/ui';
import { eur, initialen } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import type { ObjektOption } from '../../../../lib/mieter/liste';
import type { MieterListeDaten } from '../../../../lib/mieter/useMieterListe';
import {
  ANREDEN, einheitenMitBelegung, feldFehler, gesamtmiete, hinweis, kautionBetrag, KAUTION_MAX_FAKTOR, LEERER_ENTWURF, mitVorbelegung,
  schritteErledigt, wechselText, ZAHLUNGSWEISEN, type NeuerMieterEntwurf,
} from '../../../../lib/mieter/neuerMieter';
import { legeMieterAn } from '../../../../lib/mieter/neuerMieterSpeichern';
import { leseZahl } from '../../../../lib/immobilien/mietverhaeltnisFormular';

/**
 * Rückseite der Drehkarte „Neuer Mieter“ (Handoff Mieter §5.2, Screens 05/06/42):
 * Person · Mieteinheit · Vertrag & Finanzen, rechts die Live-Vorschau.
 */
export function NeuerMieterFormular({ daten, objekte, heute, onGeaendert, onGespeichert }: {
  daten: MieterListeDaten;
  objekte: readonly ObjektOption[];
  heute: string;
  /** Formular hat Eingaben (für die Rückfrage beim Abbrechen) */
  onGeaendert: (geaendert: boolean) => void;
  onGespeichert: (id: string, meldung: string) => void;
}) {
  const [e, setE] = useState<NeuerMieterEntwurf>(LEERER_ENTWURF);
  const [speichert, setSpeichert] = useState(false);
  const [toast, zeige] = useToastPill();
  const setze = <K extends keyof NeuerMieterEntwurf>(k: K) => (v: NeuerMieterEntwurf[K]) => setE(alt => ({ ...alt, [k]: v }));

  useEffect(() => { onGeaendert(JSON.stringify(e) !== JSON.stringify(LEERER_ENTWURF)); }, [e, onGeaendert]);

  const objekt = e.objektId ? objekte.find(o => o.id === e.objektId) ?? null : null;
  const einheiten = useMemo(
    () => (e.objektId ? einheitenMitBelegung(daten.einheiten, daten.mieter, e.objektId, heute) : []),
    [daten, e.objektId, heute],
  );
  const einheit = einheiten.find(x => x.id === e.einheitId) ?? null;
  const bewohner = einheit?.bewohner ?? [];
  const belegt = bewohner.length > 0;
  const vormieterName = bewohner.map(b => b.name).join(' und ');

  const [s1, s2, s3] = schritteErledigt(e);
  const h = hinweis(e);
  const fehler = feldFehler(e);
  const kalt = leseZahl(e.kalt) ?? 0;
  const name = s1 ? `${e.vorname.trim()} ${e.nachname.trim()}` : 'Neuer Mieter';

  const waehleEinheit = (id: string) => {
    const x = einheiten.find(y => y.id === id);
    setE(alt => mitVorbelegung({ ...alt, einheitId: id, art: 'wechsel' }, x && x.bewohner.length ? x.miete : null));
  };

  const speichere = async () => {
    setSpeichert(true);
    try {
      const r = await legeMieterAn(e, { einheitBezeichnung: einheit?.bezeichnung ?? '', vormieterIds: bewohner.map(b => b.id) });
      const teile = [`${e.vorname.trim()} ${e.nachname.trim()} angelegt`];
      if (belegt && e.art === 'wechsel' && !r.warnungen.some(w => w.startsWith('Mieterwechsel'))) {
        teile.push(`Auszug ${[...new Set(bewohner.map(b => b.nachname))].join(', ')} vorgemerkt`);
      }
      teile.push(...r.warnungen);
      onGespeichert(r.id, teile.join(' · '));
    } catch (err) {
      zeige(`Nicht gespeichert: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`, true);
    } finally {
      setSpeichert(false);
    }
  };

  const schritte = [
    {
      titel: 'Person',
      untertitel: 'Persönliche Stammdaten',
      erledigt: s1,
      inhalt: (
        <>
          <SegmentedControl ariaLabel="Anrede" hoehe={34} wert={e.anrede} onChange={setze('anrede')} segmente={ANREDEN.map(a => ({ key: a, label: a }))} className="self-start" />
          <FeldRaster minBreite={170}>
            <TextFeld label="Vorname" icon={User} wert={e.vorname} onChange={setze('vorname')} placeholder="Vorname" autoComplete="off" />
            <TextFeld label="Nachname" icon={User} wert={e.nachname} onChange={setze('nachname')} placeholder="Nachname" autoComplete="off" />
            <TextFeld label="Geburtsdatum" icon={Cake} type="date" wert={e.geburtsdatum} onChange={setze('geburtsdatum')} fehler={fehler.geburtsdatum} />
            <TextFeld label="E-Mail" icon={EnvelopeSimple} type="email" wert={e.email} onChange={setze('email')} placeholder="name@beispiel.de" autoComplete="off" fehler={fehler.email} />
            <TextFeld label="Telefon" icon={Phone} type="tel" wert={e.telefon} onChange={setze('telefon')} placeholder="0151 …" autoComplete="off" />
          </FeldRaster>
        </>
      ),
    },
    {
      titel: 'Mieteinheit',
      untertitel: 'Objekt suchen und Einheit zuweisen',
      erledigt: s2,
      inhalt: (
        <>
          <SearchSelect
            optionen={objekte}
            ausgewaehlt={objekt}
            onWahl={o => setE(alt => ({ ...alt, objektId: o.id, einheitId: null }))}
            onEntfernen={() => setE(alt => ({ ...alt, objektId: null, einheitId: null }))}
            platzhalter="Objekt suchen …"
            maxTreffer={8}
          />
          {objekt && einheiten.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Für dieses Objekt sind keine Einheiten angelegt.</span>
          )}
          {objekt && einheiten.length > 0 && (
            <div role="radiogroup" aria-label="Einheit" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
              {einheiten.map(x => {
                const an = x.id === e.einheitId;
                const besetzt = x.bewohner.length > 0;
                return (
                  <button key={x.id} type="button" role="radio" aria-checked={an} onClick={() => waehleEinheit(x.id)}
                    className="flex flex-col text-left cursor-pointer min-w-0"
                    style={{ borderRadius: 14, padding: '10px 12px', gap: 2, border: 'none', color: 'var(--ink)', background: an ? 'var(--acc-soft)' : 'var(--bg)', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)' }}>
                    <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>{x.bezeichnung}</span>
                    <span className="truncate" style={{ fontSize: 11, fontWeight: 700, color: besetzt ? 'var(--warn)' : 'var(--ok)' }}>
                      {besetzt ? `belegt · ${[...new Set(x.bewohner.map(b => b.nachname))].join(', ')}` : 'frei'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {belegt && (
            <div className="flex flex-col" style={{ gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Einheit ist belegt – wie soll der neue Mieter einziehen?</span>
              <div role="radiogroup" aria-label="Art des Einzugs" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                {([
                  ['wechsel', 'Mieterwechsel', ArrowsLeftRight, wechselText(vormieterName, e.mietbeginn)],
                  ['mitmieter', 'Mitmieter', Users, 'Zieht zusätzlich ein, niemand zieht aus.'],
                ] as const).map(([key, label, IconKomp, text]) => {
                  const an = e.art === key;
                  return (
                    <button key={key} type="button" role="radio" aria-checked={an} onClick={() => setze('art')(key)}
                      className="flex text-left cursor-pointer"
                      style={{ borderRadius: 16, padding: 12, gap: 10, border: 'none', color: 'var(--ink)', background: an ? 'var(--acc-soft)' : 'var(--bg)', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)' }}>
                      <IconKomp weight="fill" size={20} color={an ? 'var(--acc)' : 'var(--ink3)'} style={{ flexShrink: 0 }} />
                      <span className="flex flex-col" style={{ gap: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 800 }}>{label}</span>
                        <span style={{ fontSize: 11, color: 'var(--ink3)', lineHeight: 1.35 }}>{text}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      titel: 'Vertrag & Finanzen',
      untertitel: 'Mietbeginn, Miete, Kaution, Bankverbindung',
      erledigt: s3,
      inhalt: (
        <>
          <FeldRaster minBreite={150}>
            <TextFeld label="Mietbeginn" icon={CalendarBlank} type="date" wert={e.mietbeginn} onChange={setze('mietbeginn')} />
            <TextFeld label="Mietende (optional)" icon={CalendarX} type="date" wert={e.mietende} onChange={setze('mietende')} fehler={fehler.mietende} />
            <TextFeld label="Kaltmiete" icon={House} inputMode="decimal" wert={e.kalt} onChange={setze('kalt')} placeholder="0" suffix="€" />
            <TextFeld label="Nebenkosten" icon={Drop} inputMode="decimal" wert={e.nk} onChange={setze('nk')} placeholder="0" suffix="€" />
            <TextFeld label="Heizkosten" icon={Flame} inputMode="decimal" wert={e.hk} onChange={setze('hk')} placeholder="0" suffix="€" />
            <TextFeld label="IBAN" icon={Bank} wert={e.iban} onChange={setze('iban')} placeholder="DE00 0000 …" autoComplete="off" fehler={fehler.iban} />
            <TextFeld label="Kontoinhaber" icon={IdentificationCard} wert={e.kontoinhaber} onChange={setze('kontoinhaber')} placeholder="falls abweichend vom Mieter" autoComplete="off" />
          </FeldRaster>
          <div className="flex items-center justify-between flex-wrap" style={{ gap: 12, padding: '10px 12px 10px 16px', borderRadius: 16, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
            <span className="flex items-center" style={{ gap: 10, fontSize: 14, fontWeight: 700 }}><Vault size={18} color="var(--ink3)" />Kaution</span>
            <Stepper label="Kaution in Monatskaltmieten" wert={e.kautionFaktor} onChange={setze('kautionFaktor')} min={0} max={KAUTION_MAX_FAKTOR}
              einheit={`× Kaltmiete · ${eur(kautionBetrag(kalt, e.kautionFaktor), { nachkomma: 0 })}`} />
          </div>
          <SegmentedControl ariaLabel="Zahlungsweise" hoehe={34} wert={e.zahlungsweise} onChange={setze('zahlungsweise')} segmente={ZAHLUNGSWEISEN.map(z => ({ key: z, label: z }))} className="self-start" />
          <SchalterZeile an={false} onChange={() => undefined} disabled icon={DeviceMobile} label="Einladung ins Mieterportal direkt senden"
            zusatz="Einladung per E-Mail wird noch angebunden – den Zugang danach beim Mieter freigeben" />
        </>
      ),
    },
  ];

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <StepForm
        schritte={schritte}
        hinweis={h.text}
        bereit={h.bereit}
        speichert={speichert}
        onSpeichern={() => { void speichere(); }}
        speichernLabel={speichert ? 'Wird angelegt …' : 'Mieter anlegen'}
        vorschau={(
          <LivePreviewCard>
            <div className="flex items-center min-w-0" style={{ gap: 12 }}>
              <span className="immo-akzent flex items-center justify-center shrink-0" style={{ width: 50, height: 50, borderRadius: '50%', fontSize: 16, fontWeight: 800 }}>
                {s1 ? initialen(name) : '?'}
              </span>
              <div className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
                <span className="truncate" style={{ fontSize: 16, fontWeight: 800 }}>{name}</span>
                <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>
                  {objekt ? `${objekt.label}${einheit ? ` · ${einheit.bezeichnung}` : ''}` : 'Keine Einheit gewählt'}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap" style={{ gap: 6 }}>
              {e.mietbeginn && e.mietbeginn > heute ? <Chip ton="info">Einzug geplant</Chip> : <Chip ton="ok">Aktiv</Chip>}
              {belegt && <Chip ton={e.art === 'wechsel' ? 'warn' : 'neutral'}>{e.art === 'wechsel' ? 'Mieterwechsel' : 'Mitmieter'}</Chip>}
            </div>
            <div className="flex flex-col" style={{ gap: 8, paddingTop: 12, borderTop: '1px solid var(--line)', fontSize: 13 }}>
              <div className="flex justify-between"><span style={{ color: 'var(--ink2)' }}>Einzug</span><b className="immo-zahl">{e.mietbeginn ? deDatum(e.mietbeginn) : '–'}</b></div>
              <div className="flex justify-between"><span style={{ color: 'var(--ink2)' }}>Kaution</span><b className="immo-zahl">{eur(kautionBetrag(kalt, e.kautionFaktor), { nachkomma: 0 })}</b></div>
              <div className="flex justify-between items-baseline"><span style={{ color: 'var(--ink2)' }}>Gesamtmiete</span><b className="immo-zahl" style={{ fontSize: 20, color: 'var(--ok)' }}>{eur(gesamtmiete(e))}</b></div>
            </div>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Mieterportal: Zugang nach dem Anlegen beim Mieter freigeben.</span>
          </LivePreviewCard>
        )}
      />
    </div>
  );
}
